import { scroll, animate } from "motion";

const initWordScrollEffect = (selector = ".wysiwyg__content") => {
    const containers = document.querySelectorAll(selector);
    if (!containers.length) return;

    containers.forEach((container) => processContainer(container));
};

const processContainer = (container) => {
    wrapWordsInSpans(container);

    const words = container.querySelectorAll(".word-span");
    if (!words.length) return;

    // Create arrow element
    const arrowElement = createArrowElement();
    container.appendChild(arrowElement);

    scroll(
        (progressParam) => {
            const progress =
                typeof progressParam === "object" && progressParam?.y
                    ? progressParam.y.progress
                    : progressParam;

            const totalWords = words.length;
            const scrollY = window.scrollY;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const containerRect = container.getBoundingClientRect();

            let finalActiveWords = Math.floor(progress * totalWords);

            if (progress >= 0.999) {
                finalActiveWords = totalWords;
            } else {
                const isContainerVisible =
                    containerRect.bottom > 0 && containerRect.top < windowHeight;
                const isNearBottom = scrollY + windowHeight >= documentHeight - 20;

                if (isNearBottom && isContainerVisible) {
                    finalActiveWords = totalWords;
                } else {
                    const isPastViewport = containerRect.top < windowHeight * 0.3;
                    if (progress > 0.9 && isPastViewport) {
                        finalActiveWords = totalWords;
                    }
                }
            }

            // Find the last active word to position the arrow
            let lastActiveWord = null;
            if (finalActiveWords > 0 && finalActiveWords <= totalWords) {
                lastActiveWord = words[finalActiveWords - 1];
            } else if (finalActiveWords === totalWords && totalWords > 0) {
                lastActiveWord = words[totalWords - 1];
            }

            if (lastActiveWord) {
                updateArrowPosition(arrowElement, lastActiveWord, container);
            } else {
                // Hide arrow if no active words
                animate(
                    arrowElement,
                    {
                        opacity: 0,
                    },
                    {
                        duration: 0.2,
                        ease: "easeOut",
                    }
                );
            }

            words.forEach((word, index) => {
                const isActive = index < finalActiveWords;
                const color = document.querySelector("body").classList.contains("dark-theme")
                    ? "var(--color-white)"
                    : "var(--color-black)";
                animate(
                    word,
                    {
                        color: isActive ? color : "var(--color-tiertary-text)",
                        opacity: isActive ? 1 : 0.5,
                    },
                    {
                        duration: isActive ? 0.3 : 0.2,
                        ease: "easeOut",
                    }
                );
            });
        },
        {
            target: container,
            offset: ["start 0.75", "end 0.75"],
        }
    );
};

const createArrowElement = () => {
    const arrowWrapper = document.createElement("div");
    arrowWrapper.classList.add("animate-words__arrow");
    arrowWrapper.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="20" fill="none">
            <path fill="#D53E50" fill-rule="evenodd" d="M11.401 8.882c.401.396.401 1.044 0 1.44L2.7 18.91c-.401.396-1.053.391-1.448-.01l-.958-.975a1.03 1.03 0 0 1 .012-1.45l6.234-6.154c.401-.395.402-1.043 0-1.44L.305 2.73a1.031 1.031 0 0 1-.013-1.45l.959-.974A1.025 1.025 0 0 1 2.699.293L11.4 8.882Z" clip-rule="evenodd" />
        </svg>
    `;
    return arrowWrapper;
};

const updateArrowPosition = (arrowElement, targetWord, container) => {
    const containerRect = container.getBoundingClientRect();
    const arrowHeight = 20;

    // Prefer the last client rect (handles multi-line inline spans)
    let rect = null;
    const clientRects = targetWord.getClientRects();
    if (clientRects && clientRects.length) {
        rect = clientRects[clientRects.length - 1];
    } else {
        rect = targetWord.getBoundingClientRect();
    }

    let wordLineCenter;
    if (rect && rect.height > 0) {
        wordLineCenter = rect.top + rect.height / 2;
    }

    const relativeTop = wordLineCenter - containerRect.top;

    // Clamp within container bounds
    let unclampedTop = relativeTop - arrowHeight / 2;
    const maxTop = Math.max(0, containerRect.height - arrowHeight);
    if (!Number.isFinite(unclampedTop)) {
        unclampedTop = maxTop;
    }
    const clampedTop = Math.min(Math.max(unclampedTop, 0), maxTop);

    animate(
        arrowElement,
        { top: `${clampedTop}px`, opacity: 1 },
        { duration: 0.2, ease: "easeOut" }
    );
};

const wrapWordsInSpans = (element) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
            const parent = node.parentElement;
            if (!parent || ["SCRIPT", "STYLE"].includes(parent.tagName))
                return NodeFilter.FILTER_REJECT;
            if (parent.classList.contains("word-span")) return NodeFilter.FILTER_REJECT;
            return node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        },
    });

    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
        textNodes.push(node);
    }

    textNodes.forEach((textNode) => {
        const words = textNode.textContent.split(/(\s+)/);
        const fragment = document.createDocumentFragment();

        words.forEach((word) => {
            if (word.trim()) {
                const span = document.createElement("span");
                span.classList.add("word-span");
                span.textContent = word;
                span.style.color = "var(--color-tiertary-text)";
                span.style.opacity = "0.5";
                fragment.appendChild(span);
            } else {
                fragment.appendChild(document.createTextNode(word));
            }
        });

        textNode.parentNode?.replaceChild(fragment, textNode);
    });
};

document.addEventListener("DOMContentLoaded", () => {
    initWordScrollEffect(".animate-words");
});
